// Courbe temporelle d'une seule mesure (un graphique par unité : jamais deux axes verticaux).
// Trait 2 px + aire légère, grille discrète, périodes hors ligne grisées, réticule et infobulle au survol.
import { useEffect, useMemo, useRef, useState } from "react";
import { locale, t } from "../lib/i18n";

export interface SeriesPoint {
  ts: number;
  value: number | null;   // null = pas de mesure (trou dans la courbe)
  offline?: boolean;
}

const PAD = { top: 10, right: 12, bottom: 22, left: 64 };

/** Maximum « rond » de l'axe : 1, 2, 2,5 ou 5 × 10^n au-dessus de la valeur. */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * exp >= v) return m * exp;
  return 10 * exp;
}

function hhmm(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
}

export function TimeSeriesChart({ title, points, from, to, step, gapAfter, format, height = 170 }: {
  title: string;
  points: SeriesPoint[];
  from: number;
  to: number;
  /** durée couverte par un point (s) : largeur des zones hors ligne */
  step: number;
  /** au-delà de cet écart (s) entre deux points, la courbe est interrompue */
  gapAfter: number;
  format: (v: number) => string;
  height?: number;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    if (!wrap.current) return;
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.max(240, e.contentRect.width)));
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);

  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const max = niceMax(Math.max(0, ...points.map((p) => p.value ?? 0)) * 1.05);
  const x = (ts: number) => PAD.left + ((ts - from) / (to - from)) * plotW;
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;

  // segments continus (coupés par les trous de mesure)
  const segments = useMemo(() => {
    const out: SeriesPoint[][] = [];
    let cur: SeriesPoint[] = [];
    let prev: SeriesPoint | null = null;
    for (const p of points) {
      if (p.value === null || (prev && p.ts - prev.ts > gapAfter)) {
        if (cur.length) out.push(cur);
        cur = [];
      }
      if (p.value !== null) cur.push(p);
      prev = p;
    }
    if (cur.length) out.push(cur);
    return out;
  }, [points, gapAfter]);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const xTicks = Array.from({ length: 5 }, (_, i) => from + ((to - from) * (i + 0.5)) / 5);
  const hovered = hover !== null ? points[hover] : null;

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    if (points.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ts = from + ((e.clientX - rect.left) / rect.width) * (to - from);
    let best = 0;
    for (let i = 1; i < points.length; i++) {
      if (Math.abs(points[i]!.ts - ts) < Math.abs(points[best]!.ts - ts)) best = i;
    }
    setHover(best);
  };

  return (
    <figure className="flex flex-col gap-1">
      <figcaption className="font-cond text-[12px] font-semibold uppercase tracking-wider text-faint">{title}</figcaption>
      <div ref={wrap} className="relative">
        <svg width={width} height={height} role="img" aria-label={t("{title}, de {from} à {to}", { title, from: hhmm(from), to: hhmm(to) })}
          className="block overflow-visible">
          {/* périodes hors ligne */}
          {points.filter((p) => p.offline).map((p) => (
            <rect key={`off-${p.ts}`} x={x(p.ts)} y={PAD.top} width={Math.max(1.5, x(p.ts + step) - x(p.ts))}
              height={plotH} fill="var(--color-err)" opacity="0.16" />
          ))}
          {/* grille et graduations */}
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--color-line)"
                strokeDasharray={v === 0 ? undefined : "2 4"} />
              <text x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end" fontSize="11" fill="var(--color-faint)"
                className="num">{format(v)}</text>
            </g>
          ))}
          {xTicks.map((t) => (
            <text key={t} x={x(t)} y={height - 6} textAnchor="middle" fontSize="11" fill="var(--color-faint)" className="num">
              {hhmm(t)}
            </text>
          ))}
          {/* données */}
          {segments.map((seg, i) => {
            const line = seg.map((p) => `${x(p.ts).toFixed(1)},${y(p.value!).toFixed(1)}`).join(" ");
            const first = seg[0]!, last = seg.at(-1)!;
            return (
              <g key={i}>
                <polygon points={`${x(first.ts)},${y(0)} ${line} ${x(last.ts)},${y(0)}`} fill="var(--color-accent)" opacity="0.14" />
                <polyline points={line} fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinejoin="round" />
              </g>
            );
          })}
          {/* survol */}
          {hovered && (
            <g pointerEvents="none">
              <line x1={x(hovered.ts)} x2={x(hovered.ts)} y1={PAD.top} y2={PAD.top + plotH} stroke="var(--color-muted)" strokeWidth="1" />
              {hovered.value !== null && (
                <circle cx={x(hovered.ts)} cy={y(hovered.value)} r="4.5" fill="var(--color-accent)"
                  stroke="var(--color-surface)" strokeWidth="2" />
              )}
            </g>
          )}
          <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill="transparent"
            onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
        </svg>
        {hovered && (
          <div className="pointer-events-none absolute top-0 z-10 rounded border border-line bg-raised px-2 py-1 text-[12px] shadow-lg"
            style={{ left: Math.min(x(hovered.ts) + 10, width - 150) }}>
            <div className="num text-faint">{hhmm(hovered.ts)}</div>
            <div className="num font-medium text-ink">
              {hovered.offline ? t("Pas de signal") : hovered.value !== null ? format(hovered.value) : "–"}
            </div>
          </div>
        )}
        {points.length === 0 && (
          <div className="absolute inset-0 grid place-items-center text-[13px] text-muted">
            {t("Pas encore de mesures sur cette période.")}
          </div>
        )}
      </div>
    </figure>
  );
}
